import countries from '~/assets/constants/shopify/countries.json'
import markets from '~/assets/js/constants/markets'
import { storefrontLocales } from '~/i18n/storefrontLocales'

import type { Market } from '../constants/markets'
import type { StorefrontLocale } from '~/i18n/constants'

export type ResolvedMarket = Market

// EU is a region, so Shopify prices it through France, as the store itself does.
const REGION_COUNTRY_CODES: Record<string, string> = { eu: 'FR' }

function shopifyCountryCodeFor(countryCode: string): string {
  return REGION_COUNTRY_CODES[countryCode.toLowerCase()] || countryCode.toUpperCase()
}

export function currencyForCountryCode(countryCode: string): string | undefined {
  return countries.find((country) => country.isoCode === countryCode.toUpperCase())?.currency?.isoCode
}

const ADDITIONAL_PRICED_COUNTRY_CODES = [
  'SG',
  'SE',
  'DK',
  'NO',
  'PL',
  'AE',
  'JP',
  'CZ',
  'HK',
  'IN',
  'ZA',
  'HU',
  'MX',
]

export function pricedCountryCodes(): string[] {
  const base = Object.values(markets).map((market) => market.shopifyCountryCode)
  const additional = ADDITIONAL_PRICED_COUNTRY_CODES.filter((code) => currencyForCountryCode(code))

  return [...new Set([...base, ...additional])]
}

// A country we sell to prices through its own Market, so DE stays DE rather than
// collapsing onto the EUR market's France.
export function resolveMarketCountryCode(market: string): string {
  const value = String(market).toUpperCase()

  if (currencyForCountryCode(value)) return value

  const byCurrency = Object.values(markets).find((entry) => entry.currency === value)
  if (byCurrency) return byCurrency.shopifyCountryCode

  const locale = storefrontLocales.find(
    (entry) => entry.code.toLowerCase() === String(market).toLowerCase()
  )
  if (locale) return shopifyCountryCodeFor(locale.countryCode)

  return market
}

export function getMarket(
  localeCode: string,
  locales: StorefrontLocale[] = storefrontLocales
): ResolvedMarket {
  const locale = locales.find((entry) => entry.code === localeCode)

  if (!locale) throw new Error(`Unable to determine market for locale "${localeCode}"`)

  const shopifyCountryCode = shopifyCountryCodeFor(locale.countryCode)
  const currency = countries.find((country) => country.isoCode === shopifyCountryCode)?.currency

  return {
    currency: locale.currency,
    symbol: currency?.symbol || locale.currency,
    label: currency?.name || locale.currency,
    shopifyCountryCode,
  }
}
